import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Page, PageHeader, StatusBadge, Field, inputCls, PrimaryButton, EmptyState, Modal } from "@/components/lims";
import SigningFields, { useSigning } from "@/components/SigningFields";
import SampleQrButton from "@/components/SampleQr";
import SampleLocation from "@/components/SampleLocation";
import { ClipboardText, PaperPlaneTilt, FloppyDisk, WarningCircle } from "@phosphor-icons/react";

const FLAGS = ["", "Normal", "Abnormal", "Critical"];
// Worklist grouping order: rejected work first (it blocks review), then
// active entry, then items already waiting on the reviewer.
const GROUPS = [
  { key: "Rejected", label: "Rejected (needs correction)" },
  { key: "active", label: "To do", statuses: ["Ordered", "In Progress"] },
  { key: "Submitted", label: "Awaiting review" },
];

function groupOf(t) {
  if (t.status === "Rejected") return "Rejected";
  if (t.status === "Submitted") return "Submitted";
  return "active";
}

export default function MyWork() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [drafts, setDrafts] = useState({}); // id -> {result_value, result_unit, result_flag, notes}
  const [signingFor, setSigningFor] = useState(null);
  const [signError, setSignError] = useState("");
  const signing = useSigning();
  const [busy, setBusy] = useState({});

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/tests/worklist");
    // A current backend already returns only actionable or submitted rows.
    // Keep the same boundary client-side during rolling deployments so a
    // stale response can never resurrect the cross-persona wait banner.
    const ready = data.filter((t) =>
      t.status === "Submitted" || t.can_enter_result === true);
    setItems(ready);
    const seed = {};
    ready.forEach((t) => {
      seed[t.id] = {
        result_value: t.result_value ?? "",
        result_unit: t.result_unit ?? "",
        result_flag: t.result_flag ?? "",
        notes: t.notes ?? "",
      };
    });
    setDrafts(seed);
    setLoading(false);
  };

  useEffect(() => {
    load();
    // Opening the worklist acknowledges assignment/rejection notifications.
    api.post("/notifications/read").catch(() => {});
  }, []);

  const setDraft = (id, k) => (e) =>
    setDrafts((d) => ({ ...d, [id]: { ...d[id], [k]: e.target.value } }));

  const withBusy = async (id, fn) => {
    setBusy((b) => ({ ...b, [id]: true }));
    try {
      await fn();
      await load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy((b) => ({ ...b, [id]: false }));
    }
  };

  const saveDraft = (t) => withBusy(t.id, async () => {
    const d = drafts[t.id];
    await api.put(`/tests/${t.id}`, {
      result_value: d.result_value ?? "",
      result_unit: d.result_unit ?? "",
      result_flag: d.result_flag ?? "",
      notes: d.notes ?? "",
    });
    toast.success(`${t.test_code} draft saved`);
  });

  // Submitting a result is the §211.194(a)(7) signature of the person who
  // performed the test, so it asks for credentials rather than going straight
  // through on a click (issue #99).
  const submit = (t) => {
    const d = drafts[t.id];
    if (!(d?.result_value ?? "").trim()) {
      toast.error("Enter a result before submitting");
      return;
    }
    setSigningFor(t);
    setSignError("");
    signing.reset();
  };

  const confirmSubmit = async (e) => {
    e.preventDefault();
    const t = signingFor;
    const d = drafts[t.id];
    if (!signing.password) {
      setSignError("Your password is required to sign this result.");
      return;
    }
    return withBusy(t.id, async () => {
      try {
        await api.put(`/tests/${t.id}`, {
          result_value: d.result_value,
          result_unit: d.result_unit ?? "",
          result_flag: d.result_flag ?? "",
          notes: d.notes ?? "",
        });
        await api.post(`/tests/${t.id}/submit`, { signing: signing.block() });
        signing.reset();
        setSigningFor(null);
        toast.success(`${t.test_code} submitted for review`);
      } catch (err) {
        setSignError(formatApiError(err.response?.data?.detail) || err.message);
        throw err;
      }
    });
  };

  const grouped = GROUPS.map((g) => ({
    ...g,
    items: items.filter((t) => groupOf(t) === g.key),
  }));

  return (
    <Page>
      <PageHeader
        title="My Work"
        subtitle="Tests assigned to you, overdue first. Enter results and submit for review."
      />

      {loading ? (
        <div className="space-y-3">{[...Array(4)].map((_, i) => <div key={i} className="h-24 bg-slate-100 rounded-lg animate-pulse" />)}</div>
      ) : items.length === 0 ? (
        <EmptyState icon={ClipboardText} title="Nothing assigned to you" subtitle="Assigned tests will appear here with their due dates" />
      ) : (
        grouped.filter((g) => g.items.length > 0).map((g) => (
          <section key={g.key} className="mb-8" aria-label={g.label}>
            <h2 className="text-xs uppercase tracking-[0.15em] font-semibold text-slate-500 mb-3">
              {g.label} <span className="text-slate-400">({g.items.length})</span>
            </h2>
            <div className="space-y-3">
              {g.items.map((t) => {
                const d = drafts[t.id] || {};
                const editable = ["Ordered", "In Progress", "Rejected"].includes(t.status);
                const canEnterResult = editable && t.can_enter_result === true;
                return (
                  <div key={t.id} data-testid={`work-item-${t.test_code}`} className="bg-white rounded-lg border border-slate-200 p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-mono text-xs text-slate-500">{t.test_code}</span>
                        <span className="font-medium text-slate-900">{t.assay_name}</span>
                        <StatusBadge status={t.status} />
                        {t.overdue && (
                          <span data-testid={`overdue-${t.test_code}`} className="inline-flex items-center gap-1 text-xs font-medium text-status-critical bg-status-critical/10 border border-status-critical/30 px-2 py-0.5 rounded-full">
                            <WarningCircle size={12} weight="fill" /> Overdue
                          </span>
                        )}
                      </div>
                      {t.sample_id && (
                        <SampleQrButton sampleId={t.sample_id} sampleCode={t.sample_code} />
                      )}
                    </div>

                    {/* Four columns, one field per cell, so every row fills
                        evenly and the result inputs below land on the same
                        column edges. The spec is a field like any other: as a
                        line floating under the grid it read as a footnote to
                        whichever cell happened to sit above it. */}
                    <dl className="mt-3 grid grid-cols-2 gap-x-6 gap-y-3 text-xs sm:grid-cols-4">
                      <div>
                        <dt className="uppercase tracking-wider text-slate-400">Sample ID</dt>
                        <dd className="mt-0.5 font-medium text-slate-700">
                          <Link
                            data-testid={`sample-id-${t.test_code}`}
                            to={`/samples/${t.sample_id}`}
                            className="font-mono hover:text-slate-900 hover:underline"
                          >
                            {t.sample_code || "—"}
                          </Link>
                        </dd>
                      </div>
                      <div className="min-w-0">
                        <dt className="uppercase tracking-wider text-slate-400">Location</dt>
                        <dd data-testid={`location-${t.test_code}`} className="mt-0.5 font-medium text-slate-700">
                          <SampleLocation sample={t} />
                        </dd>
                      </div>
                      <div>
                        <dt className="uppercase tracking-wider text-slate-400">Sample type</dt>
                        <dd data-testid={`sample-type-${t.test_code}`} className="mt-0.5 font-medium text-slate-700">
                          {t.sample_type || "—"}
                        </dd>
                      </div>
                      <div>
                        <dt className="uppercase tracking-wider text-slate-400">Due</dt>
                        <dd className="mt-0.5 font-medium text-slate-700">
                          {t.due_date ? new Date(t.due_date).toLocaleDateString() : "—"}
                        </dd>
                      </div>
                      <div className="min-w-0">
                        <dt className="uppercase tracking-wider text-slate-400">Sample</dt>
                        <dd className="mt-0.5 font-medium text-slate-700">{t.sample_name || "—"}</dd>
                      </div>
                      <div className="min-w-0">
                        <dt className="uppercase tracking-wider text-slate-400">Client</dt>
                        <dd className="mt-0.5 font-medium text-slate-700">{t.client_name || "—"}</dd>
                      </div>
                      <div>
                        <dt className="uppercase tracking-wider text-slate-400">Test request</dt>
                        <dd data-testid={`test-request-${t.test_code}`} className="mt-0.5 font-medium text-slate-700">
                          {t.assay_name || "—"}
                        </dd>
                      </div>
                      {(t.spec_min != null || t.spec_max != null) && (
                        <div>
                          <dt className="uppercase tracking-wider text-slate-400">Spec</dt>
                          <dd data-testid={`spec-${t.test_code}`} className="mt-0.5 font-mono font-medium text-slate-700">
                            {t.spec_min ?? ""}–{t.spec_max ?? ""} {t.result_unit || ""}
                          </dd>
                        </div>
                      )}
                    </dl>

                    {t.status === "Rejected" && t.rejection_reason && (
                      <div data-testid={`rejection-reason-${t.test_code}`} role="alert" className="mt-3 rounded-md border border-status-critical/30 bg-status-critical/10 px-3 py-2 text-sm text-status-critical">
                        <span className="font-medium">Rejected by {t.rejected_by}:</span> {t.rejection_reason}
                      </div>
                    )}

                    {canEnterResult ? (
                      /* The actions get their own row rather than a fifth grid
                         cell. Sharing the field row squeezed two buttons into
                         one column, which is what wrapped "Save draft" onto a
                         second line and pushed the inputs off the metadata's
                         column edges. */
                      <div className="mt-4 border-t border-slate-100 pt-4">
                        <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                          <Field label="Result">
                            <input data-testid={`result-input-${t.test_code}`} value={d.result_value ?? ""} onChange={setDraft(t.id, "result_value")} className={inputCls} />
                          </Field>
                          <Field label="Unit">
                            <input data-testid={`unit-input-${t.test_code}`} value={d.result_unit ?? ""} onChange={setDraft(t.id, "result_unit")} className={inputCls} />
                          </Field>
                          <Field label="Flag">
                            <select data-testid={`flag-select-${t.test_code}`} value={d.result_flag ?? ""} onChange={setDraft(t.id, "result_flag")} className={inputCls}>
                              {FLAGS.map((f) => <option key={f} value={f}>{f || "None"}</option>)}
                            </select>
                          </Field>
                          <Field label="Comment">
                            <input data-testid={`notes-input-${t.test_code}`} value={d.notes ?? ""} onChange={setDraft(t.id, "notes")} className={inputCls} />
                          </Field>
                        </div>
                        <div className="mt-3 flex justify-end gap-2">
                          <button
                            type="button"
                            data-testid={`save-draft-${t.test_code}`}
                            onClick={() => saveDraft(t)}
                            disabled={busy[t.id]}
                            className="inline-flex items-center gap-1.5 whitespace-nowrap px-3 py-2 rounded-md text-sm text-slate-600 border border-slate-200 hover:bg-slate-50 transition-colors disabled:opacity-60"
                          >
                            <FloppyDisk size={16} /> Save draft
                          </button>
                          <PrimaryButton data-testid={`submit-${t.test_code}`} type="button" onClick={() => submit(t)} disabled={busy[t.id] || !(d.result_value || "").trim()}>
                            <PaperPlaneTilt size={16} /> Submit
                          </PrimaryButton>
                        </div>
                      </div>
                    ) : (
                      <div className="mt-3 text-sm text-slate-500">
                        Result: <span className="font-medium text-slate-900">{t.result_value} {t.result_unit}</span>
                        {t.result_flag && <StatusBadge status={t.result_flag} />}
                        <span className="ml-2 text-slate-400">submitted {t.submitted_date ? new Date(t.submitted_date).toLocaleString() : ""}</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        ))
      )}

      <Modal
        open={!!signingFor}
        onClose={() => setSigningFor(null)}
        title={signingFor ? `Sign and submit ${signingFor.test_code}` : ""}
        testid="submit-signing-modal"
      >
        {signingFor && (
          <form onSubmit={confirmSubmit} noValidate className="space-y-4">
            <div className="rounded-md bg-slate-50 border border-slate-200 p-3 text-sm">
              <div className="text-slate-500">{signingFor.assay_name} · {signingFor.sample_code}</div>
              <div className="font-medium text-slate-900 mt-1">
                Result: {drafts[signingFor.id]?.result_value} {drafts[signingFor.id]?.result_unit}
              </div>
            </div>
            <SigningFields
              signing={signing}
              meaning="Test performed and result recorded"
              error={signError}
              disabled={!!busy[signingFor.id]}
            />
            <div className="flex justify-end gap-2 pt-2">
              <button type="button" onClick={() => setSigningFor(null)} className="px-4 py-2 rounded-md text-sm text-slate-600 hover:bg-slate-100 transition-colors">Cancel</button>
              <PrimaryButton data-testid="confirm-submit-signature" disabled={!!busy[signingFor.id]}>
                Sign and submit
              </PrimaryButton>
            </div>
          </form>
        )}
      </Modal>
    </Page>
  );
}
